// Master Apps · Gastos — custo estimado dos projetos no Railway.
//
// A tela responde uma pergunta só: quanto a infraestrutura da TI custa por mês e
// onde esse dinheiro está. Por isso ela mostra o TOTAL DO WORKSPACE (todos os
// projetos, mapeados ou não), a tabela por projeto em ordem decrescente de custo
// e a quebra por recurso (memória, CPU, egress) com as quantidades que a
// originaram — um valor sozinho não permite conferir nada.
//
// Os projetos NÃO MAPEADOS (sem repositório configurado no deploy) ganham seção
// própria em vez de sair da lista: eles existem e consomem, e esconder daria um
// total falso — que é justamente o número que se leva para a diretoria.
//
// TRÊS ESTADOS que não são erro de rede e por isso vivem aqui, não no
// `ScreenError` da página: `semToken` (falta configurar o token da API do
// Railway), `precoDiscoConfirmado: false` (disco e backup ficam fora do total) e
// lista vazia.
//
// LEITURA VISUAL (por que existem gráficos aqui): duas tabelas de oito colunas
// de números são corretas e ilegíveis ao mesmo tempo — para saber "para onde vai
// o dinheiro" era preciso ler célula por célula. Então a tela abre com UM
// número-herói (o total), uma barra de composição part-to-whole com os cinco
// componentes cobrados e um ranking horizontal por projeto; as tabelas
// permanecem embaixo como a visão exata e conferível (e, no tema claro, como o
// relevo que compensa as cores de croma baixo — ver `COMPONENTES`).
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { IconAlert, IconInfo, IconRefresh } from '@/components/icons';
import { ButtonGhost, Sidebar, TopBar } from '@/components/primitives';
import {
  type RailwayCostDetail,
  type RailwayCostQuantities,
  type RailwayCosts,
  type RailwayProjectCost,
  repoSlug,
  totalUsd,
  unmappedCosts,
} from '@/hooks/use-railway-costs';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { formatDateTimeBR, formatUsdBR } from '@/lib/format';
import type { ScreenProps } from '@/lib/theme';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
} from '@/lib/theme';

const WARN = '#ca8a04';

/**
 * Os CINCO componentes cobrados, em SLOT FIXO — memória, CPU, egress, disco e
 * backup sempre nesta ordem e sempre com esta cor, em toda a tela (barra de
 * composição, legenda e cabeçalho das tabelas). Slot fixo e não ciclo por rank:
 * se a cor mudasse com a ordenação, comparar duas leituras da mesma tela em dias
 * diferentes deixaria de funcionar.
 *
 * As cores foram validadas contra as superfícies reais do app (card `#ffffff` no
 * claro, `#111114` no escuro): faixa de luminosidade, piso de croma, separação
 * para daltonismo (pior par adjacente ΔE 9,1 claro / 8,4 escuro) e piso de visão
 * normal (19,6 / 19,3). O tema escuro é ESCOLHIDO, não derivado: nada de filtro,
 * opacidade ou `liftForDark` em cima da cor clara.
 *
 * No claro, três das cinco ficam abaixo de 3:1 de contraste com o branco — o que
 * é aceitável porque a tela dá relevo por outro caminho: legenda rotulada com
 * nome e valor, e as duas tabelas com todos os cinco componentes. Não remova nem
 * a legenda nem as tabelas.
 */
interface Componente {
  nome: string;
  usd: keyof RailwayCostDetail;
  qtd: keyof RailwayCostQuantities;
  unidade: string;
  claro: string;
  escuro: string;
}

const COMPONENTES: readonly Componente[] = [
  {
    nome: 'Memória',
    usd: 'memoriaUsd',
    qtd: 'memoriaGbMes',
    unidade: 'GB·mês',
    claro: '#2a78d6',
    escuro: '#3987e5',
  },
  {
    nome: 'CPU',
    usd: 'cpuUsd',
    qtd: 'cpuVcpuMes',
    unidade: 'vCPU·mês',
    claro: '#eb6834',
    escuro: '#d95926',
  },
  {
    nome: 'Egress',
    usd: 'egressUsd',
    qtd: 'egressGb',
    unidade: 'GB',
    claro: '#1baf7a',
    escuro: '#199e70',
  },
  {
    nome: 'Disco',
    usd: 'discoUsd',
    qtd: 'discoGbMes',
    unidade: 'GB·mês',
    claro: '#eda100',
    escuro: '#c98500',
  },
  {
    nome: 'Backup',
    usd: 'backupUsd',
    qtd: 'backupGbMes',
    unidade: 'GB·mês',
    claro: '#e87ba4',
    escuro: '#d55181',
  },
];

/** Cor do slot no tema em uso — coluna escolhida, nunca a clara transformada. */
function corDe(c: Componente, dark: boolean): string {
  return dark ? c.escuro : c.claro;
}

/**
 * Preto ou branco sobre um preenchimento, pela luminância relativa (WCAG). Só
 * usada para o rótulo DENTRO do segmento da composição: nenhuma cor de série
 * vira cor de texto nesta tela — aqui a cor de série é o FUNDO.
 */
function textoSobre(hex: string): string {
  const canal = (i: number) => {
    const v = Number.parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * canal(0) + 0.7152 * canal(1) + 0.0722 * canal(2);
  return l > 0.4 ? '#0a0a0b' : '#ffffff';
}

/**
 * Grade da tabela por projeto. As CINCO colunas de custo (memória, CPU, egress,
 * disco e backup) têm a mesma largura de propósito: elas se comparam entre si e
 * somam a última coluna — que é mais larga e em negrito, porque é a coluna que
 * responde a pergunta da tela.
 */
const GRID = '1.45fr 1.3fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr 1.15fr';

/**
 * Piso de largura da tabela: oito colunas com valores em fonte mono não cabem
 * abaixo disto sem virar reticências. Acima do celular a saída é rolar de lado
 * (o mesmo trato do inventário); no celular a tabela vira pilha de cards.
 */
const MIN_WIDTH = 980;

const SCROLLER: CSSProperties = { overflowX: 'auto', scrollbarWidth: 'thin' };

/** Número com no máximo 3 casas, em pt-BR — as quantidades são frações de GB. */
function qtd(value: number | null | undefined, unidade: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} ${unidade}`;
}

/** Participação percentual, em pt-BR: `0,0%` … `100,0%`. Total zero → '—'. */
function participacao(valor: number, total: number): string {
  if (!Number.isFinite(valor) || !Number.isFinite(total) || total <= 0) return '—';
  return `${((valor / total) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

const HORA_MS = 1000 * 60 * 60;

/**
 * Idade do snapshot em ms, ou `null` sem carimbo. Nunca negativa: relógio do
 * navegador adiantado em relação ao servidor não pode virar "há -2 h".
 */
function idadeMs(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Date.now() - t);
}

/** "agora mesmo" · "há 12 min" · "há 3 h" · "há 2 dias". */
function idadeTexto(ms: number | null): string {
  if (ms === null) return 'nunca lido';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora mesmo';
  if (min < 60) return `há ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/**
 * A partir de quanto tempo o snapshot é considerado VELHO. O servidor renova a
 * cada hora; passando de três, a leitura só continua igual porque a API do
 * Railway falhou e a rota preferiu servir o snapshot antigo a devolver erro —
 * situação que não gera nenhum aviso por si só, e por isso a tela avisa.
 */
const IDADE_SUSPEITA_MS = 3 * HORA_MS;

/** Faixa de aviso (amarela) ou informativa (neutra) no topo da tela. */
function Aviso({
  dark,
  tone,
  titulo,
  children,
}: Readonly<{
  dark: boolean;
  tone: 'warn' | 'info';
  titulo: string;
  children: ReactNode;
}>) {
  const p = makePalette(dark);
  const warn = tone === 'warn';
  const color = warn ? (dark ? '#fde68a' : '#78350f') : p.textMuted;
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 10,
        marginBottom: 14,
        background: warn ? (dark ? '#1a1408' : '#fffbeb') : p.surface2,
        border: `1px solid ${warn ? (dark ? '#78350f' : '#fde68a') : p.border}`,
        color,
        fontSize: 12.5,
        lineHeight: 1.6,
      }}
    >
      <span style={{ display: 'flex', flexShrink: 0, marginTop: 2 }}>
        {warn ? <IconAlert size={15} /> : <IconInfo size={15} />}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, marginBottom: 3 }}>{titulo}</div>
        <div>{children}</div>
      </div>
    </div>
  );
}

// ─── Tooltip dos gráficos ─────────────────────────────────────────────────────

interface Dica {
  titulo: string;
  valor: string;
  parte: string;
  x: number;
  y: number;
}

/**
 * Tooltip único da tela, em `position: fixed` seguindo o cursor. Fixed e não
 * absolute de propósito: os gráficos ficam dentro do container com `overflow:
 * auto` do conteúdo, e um painel absoluto seria cortado na borda do card.
 * `pointerEvents: none` para não roubar o próprio hover que o abriu.
 */
function Tooltip({ dica, dark }: Readonly<{ dica: Dica | null; dark: boolean }>) {
  if (!dica) return null;
  const p = makePalette(dark);
  const larguraJanela = typeof window === 'undefined' ? 1280 : window.innerWidth;
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left: Math.max(8, Math.min(dica.x + 14, larguraJanela - 236)),
        top: Math.max(8, dica.y - 58),
        zIndex: 60,
        pointerEvents: 'none',
        width: 220,
        background: p.surface,
        border: `1px solid ${p.borderStrong}`,
        borderRadius: 8,
        padding: '8px 10px',
        boxShadow: dark ? '0 4px 14px rgba(0,0,0,0.5)' : '0 4px 14px rgba(0,0,0,0.12)',
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          color: p.text,
          marginBottom: 3,
          overflowWrap: 'anywhere',
        }}
      >
        {dica.titulo}
      </div>
      <div
        style={{
          ...numeroTabular,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          fontSize: 11,
          color: p.textMuted,
        }}
      >
        <span>{dica.valor}</span>
        <span style={{ color: p.muted }}>{dica.parte} do total</span>
      </div>
    </div>
  );
}

/** Largura medida de um elemento — a composição decide o rótulo interno por ela. */
function useLarguraMedida(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [largura, setLargura] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setLargura(el.getBoundingClientRect().width);
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, largura];
}

// ─── Número-herói e indicadores subordinados ──────────────────────────────────

/**
 * O ÚNICO número grande da tela. Fonte sans do app (nunca serif/display) e
 * figuras PROPORCIONAIS: `tabular-nums` num número deste corpo abre buracos
 * entre os dígitos, e aqui não há coluna nenhuma para alinhar.
 */
function NumeroHeroi({
  dark,
  accent,
  rotulo,
  valor,
  nota,
  compacto,
}: Readonly<{
  dark: boolean;
  accent: string;
  rotulo: string;
  valor: string;
  nota: string;
  compacto: boolean;
}>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        flex: compacto ? '1 1 100%' : '1 1 300px',
        minWidth: 0,
        background: p.surface,
        border: `1px solid ${accent}55`,
        borderRadius: 12,
        padding: compacto ? '14px 16px 16px' : '18px 20px 20px',
      }}
    >
      <div style={{ fontSize: 12.5, color: p.textMuted, letterSpacing: '-0.005em' }}>{rotulo}</div>
      <div
        data-heroi="total"
        style={{
          fontFamily: fontSans,
          fontSize: compacto ? 48 : 56,
          lineHeight: 1.02,
          fontWeight: 700,
          letterSpacing: '-0.035em',
          fontVariantNumeric: 'proportional-nums',
          color: p.text,
          marginTop: 8,
          overflowWrap: 'anywhere',
        }}
      >
        {valor}
      </div>
      <div style={{ fontSize: 11.5, color: p.muted, marginTop: 8 }}>{nota}</div>
    </div>
  );
}

/** Indicador secundário: subordinado ao herói, por corpo e por peso. */
function Resumo({
  dark,
  rotulo,
  valor,
  nota,
}: Readonly<{
  dark: boolean;
  rotulo: string;
  valor: string;
  nota?: string;
}>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        flex: '1 1 150px',
        minWidth: 0,
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        padding: 14,
        // Centrado na vertical porque o cartão estica até a altura do herói: com
        // o conteúdo colado no topo, o indicador virava uma caixa meio vazia.
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          ...rotuloCaixaAlta,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: p.muted,
        }}
      >
        {rotulo}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: p.text,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 6,
        }}
      >
        {valor}
      </div>
      {nota && <div style={{ fontSize: 11, color: p.muted, marginTop: 4 }}>{nota}</div>}
    </div>
  );
}

/** Moldura dos dois gráficos: título, subtítulo e o desenho. */
function Cartao({
  dark,
  titulo,
  subtitulo,
  children,
}: Readonly<{ dark: boolean; titulo: string; subtitulo: string; children: ReactNode }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 12,
        padding: byViewport(vp, { mobile: 14, desktop: 18 }),
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 600, color: p.text, letterSpacing: '-0.012em' }}>
        {titulo}
      </div>
      <div style={{ fontSize: 11.5, color: p.muted, marginTop: 2, lineHeight: 1.5 }}>
        {subtitulo}
      </div>
      <div style={{ marginTop: 14 }}>{children}</div>
    </div>
  );
}

// ─── Barra de composição do workspace ─────────────────────────────────────────

/**
 * Uma barra empilhada com os CINCO componentes somando o total — part-to-whole
 * legítimo (cinco segmentos, abaixo do teto de seis).
 *
 * Os segmentos são separados por um GAP de 2px que mostra a superfície do card:
 * é o gap que separa as marcas, não uma borda desenhada em volta delas (borda
 * clara em volta de preenchimento escuro engorda a marca e falseia a área).
 *
 * A LEGENDA é obrigatória com cinco séries, e o texto dela usa token de texto —
 * a identidade vem do quadradinho de cor ao lado, nunca da cor da palavra.
 */
function BarraComposicao({
  dark,
  totais,
  total,
  onDica,
  onSair,
}: Readonly<{
  dark: boolean;
  totais: readonly number[];
  total: number;
  onDica: (d: Dica) => void;
  onSair: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [ref, largura] = useLarguraMedida();
  const altura = byViewport(vp, { mobile: 26, desktop: 32 });

  if (total <= 0) {
    return (
      <div style={{ fontSize: 12, color: p.muted }}>
        Sem valor a compor — nenhum consumo foi lido.
      </div>
    );
  }

  return (
    <div>
      <div
        ref={ref}
        role="img"
        aria-label="Composição do custo do workspace por componente cobrado"
        style={{
          display: 'flex',
          gap: 2,
          height: altura + 12,
          alignItems: 'center',
          background: p.surface,
        }}
      >
        {COMPONENTES.map((c, i) => {
          const valor = totais[i] ?? 0;
          const fracao = valor / total;
          // Piso de 3px: um componente de 0,2% não pode virar zero pixel e
          // desaparecer do part-to-whole (mas também não pode ganhar corpo).
          const larguraSegmento = Math.max(fracao * largura, valor > 0 ? 3 : 0);
          if (larguraSegmento <= 0) return null;
          const cor = corDe(c, dark);
          // Rótulo interno em ESCADA: cabendo, mostra nome + participação;
          // cabendo pouco, só a participação; não cabendo, nada — a legenda
          // abaixo e as tabelas carregam a leitura (nada de `overflow: hidden`
          // para fingir que caberia, porque cortar as primeiras letras é pior
          // que não ter rótulo).
          //
          // A largura necessária é derivada do TEXTO de cada segmento, não de um
          // número fixo: "Memória 82,4%" pede mais espaço que "CPU 6%", e um
          // limite único trataria os dois igual. ~6,4px por caractere é a média
          // medida da sans em 10,5px/700, mais 16px de respiro dos dois lados.
          const parte = participacao(valor, total);
          const precisaPara = (t: string) => t.length * 6.4 + 16;
          const rotuloInterno =
            larguraSegmento >= precisaPara(`${c.nome} ${parte}`)
              ? `${c.nome} ${parte}`
              : larguraSegmento >= precisaPara(parte)
                ? parte
                : null;
          const dica = (e: { clientX: number; clientY: number }) =>
            onDica({
              titulo: c.nome,
              valor: formatUsdBR(valor) || '—',
              parte,
              x: e.clientX,
              y: e.clientY,
            });
          return (
            <div
              key={c.usd}
              // A faixa inteira (12px mais alta que a marca) é o alvo de hover.
              onMouseMove={dica}
              onMouseLeave={onSair}
              data-composicao={c.nome}
              style={{
                width: `${fracao * 100}%`,
                minWidth: valor > 0 ? 3 : 0,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div
                data-marca={c.nome}
                style={{
                  width: '100%',
                  height: altura,
                  background: cor,
                  borderRadius:
                    i === 0 ? '4px 0 0 4px' : i === COMPONENTES.length - 1 ? '0 4px 4px 0' : 0,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: rotuloInterno ? 8 : 0,
                }}
              >
                {rotuloInterno && (
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: textoSobre(cor),
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {rotuloInterno}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px 18px',
          marginTop: 14,
        }}
      >
        {COMPONENTES.map((c, i) => {
          const valor = totais[i] ?? 0;
          return (
            <div key={c.usd} style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: corDe(c, dark),
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 11.5, color: p.textMuted, whiteSpace: 'nowrap' }}>
                {c.nome}
              </span>
              <span
                style={{
                  ...numeroTabular,
                  fontSize: 11.5,
                  color: p.text,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatUsdBR(valor) || '—'}
              </span>
              {/* A participação é a última coluna da legenda: uma linha por
                  componente, então a figura tabular alinha os '%'. Não era mono
                  antes — só não tinha figura tabular. */}
              <span
                style={{
                  ...numeroTabular,
                  fontSize: 10.5,
                  color: p.muted,
                  whiteSpace: 'nowrap',
                }}
              >
                {participacao(valor, total)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Ranking horizontal por projeto ───────────────────────────────────────────

/** Quantos projetos aparecem nomeados antes da dobra em "Outros N projetos". */
const RANKING_VISIVEL = 12;

/** Quantas barras ficam no acento CHEIO — as que dominam o gasto. */
const RANKING_DESTAQUE = 3;

/**
 * Passo RECESSIVO das barras do ranking: mesmo matiz do acento, um degrau para
 * trás. Doze blocos grandes no acento cheio liam como parede de tinta saturada —
 * e a saturação não era dado nenhum, porque o comprimento já carrega a
 * magnitude. Então a tela aplica ÊNFASE: as três primeiras no acento, o resto
 * neste passo e a barra dobrada ("Outros N") mais recessiva ainda.
 *
 * NÃO É PALETA CATEGÓRICA — não "conserte" isto. A regra de que cor segue a
 * ENTIDADE e nunca o rank vale para paleta categórica, onde a cor carrega
 * identidade; aqui a série é ÚNICA (custo do mês) e a cor não identifica nada,
 * então graduar por posição é legítimo e é justamente o que dá hierarquia. Quem
 * carrega identidade nesta tela é a paleta de `COMPONENTES`, e ela sim está em
 * slot fixo.
 *
 * Valores ESCOLHIDOS por tema (como o resto da tela), não derivados por
 * opacidade em cima do acento — alfa sobre a superfície escura lavava a barra.
 * Medidos contra as superfícies reais: no claro 3,24:1 com o card branco (o
 * acento cheio dá 5,88:1) e no escuro 2,94:1 com `#111114` (cheio: 4,62:1). A
 * cauda recua sem desaparecer.
 */
const RANKING_RECESSIVO = { claro: '#dd6b7d', escuro: '#b02b44' } as const;

interface ItemRanking {
  nome: string;
  valor: number;
  /** Quantos projetos foram DOBRADOS nesta barra (0 nas barras nominais). */
  dobrados: number;
}

/**
 * Os 12 maiores nominais e o resto numa barra final rotulada com a contagem.
 * Não é esconder dado: a tabela abaixo continua com todos os projetos, e o
 * rótulo diz exatamente quantos entraram na dobra.
 */
function montarRanking(ordenados: readonly RailwayProjectCost[]): ItemRanking[] {
  const itens: ItemRanking[] = ordenados
    .slice(0, RANKING_VISIVEL)
    .map((c) => ({ nome: c.projetoRailway, valor: c.estimadoMesUsd || 0, dobrados: 0 }));
  const resto = ordenados.slice(RANKING_VISIVEL);
  if (resto.length > 0) {
    itens.push({
      nome: `Outros ${resto.length} projeto${resto.length === 1 ? '' : 's'}`,
      valor: resto.reduce((acc, c) => acc + (c.estimadoMesUsd || 0), 0),
      dobrados: resto.length,
    });
  }
  return itens;
}

/**
 * Ranking horizontal, SÉRIE ÚNICA (custo do mês), ordem decrescente.
 *
 *  - horizontal e não coluna porque os nomes de projeto são longos;
 *  - série única ⇒ sem caixa de legenda (o título do cartão já diz o que é) e
 *    sem as cores da composição: aqui a cor não carrega identidade, o
 *    COMPRIMENTO carrega. Por isso o acento do app, que já trata claro/escuro;
 *  - barra fina (24px de teto) dentro de uma faixa mais alta, para sobrar ar;
 *  - ponta arredondada só na extremidade do dado, quadrada na linha de base;
 *  - valor apenas na PONTA da barra — é o rótulo seletivo permitido;
 *  - eixo hairline de 1px SÓLIDO (nunca tracejado), um passo fora da superfície,
 *    e dimensionado dentro do cartão para não criar rolagem interna.
 */
function RankingProjetos({
  dark,
  accent,
  itens,
  total,
  onDica,
  onSair,
}: Readonly<{
  dark: boolean;
  accent: string;
  itens: readonly ItemRanking[];
  total: number;
  onDica: (d: Dica) => void;
  onSair: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();

  if (itens.length === 0) {
    return <div style={{ fontSize: 12, color: p.muted }}>Nenhum projeto para ranquear.</div>;
  }

  const maximo = itens.reduce((m, i) => Math.max(m, i.valor), 0);
  const escala = maximo > 0 ? maximo : 1;
  // No celular o nome vai para cima da barra: 110px de coluna de nome a 390px
  // deixariam a barra sem curso e o rótulo colidindo com o valor.
  const nomeEmCima = vp.mobile;
  const colunaNome = byViewport(vp, { mobile: 0, tablet: 150, desktop: 200 });
  const espessura = byViewport(vp, { mobile: 14, tablet: 18, desktop: 22 });
  // A faixa é maior que a marca de propósito: barra encostando em barra lê como
  // bloco saturado, e o teto de 24px só vale se sobrar ar em volta.
  const faixa = espessura + 12;
  // Reserva para o valor na ponta: a barra cresce em cima de `100% - reserva`,
  // então nem a barra mais longa empurra o número para fora do cartão.
  const reservaValor = byViewport(vp, { mobile: 86, desktop: 92 });

  const eixo = (rotulos: readonly string[]) => (
    <div style={{ display: 'flex', marginLeft: nomeEmCima ? 0 : colunaNome + 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ height: 1, background: p.chartGrid }} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 5,
            marginRight: reservaValor,
          }}
        >
          {rotulos.map((r) => (
            <span
              key={r}
              style={{
                ...numeroTabular,
                fontSize: 9.5,
                color: p.muted,
              }}
            >
              {r}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Faixas separadas por gap de 2px na cor da superfície (nunca borda). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, background: p.surface }}>
        {itens.map((item, i) => {
          const fracao = item.valor / escala;
          // Ênfase: topo no acento cheio, cauda no passo recessivo do MESMO
          // matiz (nunca cinza, que sugeriria "outra categoria") e a dobra mais
          // recessiva de todas.
          const cor =
            item.dobrados > 0
              ? `${accent}55`
              : i < RANKING_DESTAQUE
                ? accent
                : dark
                  ? RANKING_RECESSIVO.escuro
                  : RANKING_RECESSIVO.claro;
          const dica = (e: { clientX: number; clientY: number }) =>
            onDica({
              titulo: item.dobrados > 0 ? `${item.nome} (soma)` : item.nome,
              valor: formatUsdBR(item.valor) || '—',
              parte: participacao(item.valor, total),
              x: e.clientX,
              y: e.clientY,
            });
          return (
            <div
              key={item.nome}
              // Alvo de hover = a faixa inteira, maior que a marca.
              onMouseMove={dica}
              onMouseLeave={onSair}
              data-ranking={item.nome}
              data-valor={item.valor}
              style={{
                display: 'flex',
                flexDirection: nomeEmCima ? 'column' : 'row',
                alignItems: nomeEmCima ? 'stretch' : 'center',
                gap: nomeEmCima ? 3 : 12,
                minHeight: nomeEmCima ? undefined : faixa,
                paddingBottom: nomeEmCima ? 6 : 0,
              }}
            >
              <div
                style={{
                  width: nomeEmCima ? undefined : colunaNome,
                  flexShrink: 0,
                  fontSize: nomeEmCima ? 11.5 : 12,
                  fontWeight: item.dobrados > 0 ? 500 : 600,
                  fontStyle: item.dobrados > 0 ? 'italic' : 'normal',
                  color: item.dobrados > 0 ? p.muted : p.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={item.nome}
              >
                {item.nome}
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  height: faixa,
                  // Base ÚNICA de onde todas as barras crescem — hairline sólido.
                  borderLeft: `1px solid ${p.chartGrid}`,
                }}
              >
                <div
                  style={{
                    width: `calc((100% - ${reservaValor}px) * ${Math.max(fracao, 0)})`,
                    minWidth: item.valor > 0 ? 3 : 0,
                    height: espessura,
                    background: cor,
                    // Ponta arredondada no dado, quadrada na linha de base.
                    borderRadius: '0 4px 4px 0',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    ...numeroTabular,
                    marginLeft: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    color: p.text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatUsdBR(item.valor) || '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 6 }}>
        {eixo(['US$ 0', formatUsdBR(maximo / 2), formatUsdBR(maximo)])}
      </div>
    </div>
  );
}

// ─── Tabelas ──────────────────────────────────────────────────────────────────

/** Célula "valor em dólar + quantidade que o gerou". */
function Celula({
  dark,
  valor,
  detalhe,
  forte = false,
}: Readonly<{ dark: boolean; valor: string; detalhe?: string; forte?: boolean }>) {
  const p = makePalette(dark);
  return (
    <div style={{ textAlign: 'right', minWidth: 0 }}>
      <div
        style={{
          ...numeroTabular,
          fontSize: forte ? 13 : 11.5,
          fontWeight: forte ? 700 : 400,
          // Detalhe RECUA e total AVANÇA: as cinco parcelas em texto secundário,
          // o total no texto primário e em negrito.
          color: forte ? p.text : p.textMuted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {valor}
      </div>
      {detalhe && (
        <div
          style={{
            ...numeroTabular,
            fontSize: 9.5,
            color: p.muted,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {detalhe}
        </div>
      )}
    </div>
  );
}

const CABECALHO: CSSProperties = {
  ...rotuloCaixaAlta,
  fontSize: 10,
  fontWeight: 700,
  color: 'inherit',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

/**
 * Cabeçalho de coluna de componente: quadradinho da cor do slot + nome. É o que
 * amarra a tabela à barra de composição sem colorir uma única palavra.
 */
function CabecalhoComponente({ dark, c }: Readonly<{ dark: boolean; c: Componente }>) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
      <span
        aria-hidden="true"
        style={{ width: 8, height: 8, borderRadius: 2, background: corDe(c, dark), flexShrink: 0 }}
      />
      <span>{c.nome}</span>
    </div>
  );
}

/** Uma linha da tabela (tablet/desktop). */
function LinhaProjeto({
  custo,
  dark,
  divider,
}: Readonly<{ custo: RailwayProjectCost; dark: boolean; divider: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 12,
        padding: '9px 16px',
        alignItems: 'center',
        borderBottom: divider ? `1px solid ${p.border}` : 'none',
        fontSize: 12,
      }}
    >
      <div
        style={{
          fontWeight: 600,
          color: p.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {custo.projetoRailway}
      </div>
      <div
        style={{
          fontFamily: fontMono,
          fontSize: 10.5,
          color: p.muted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={custo.repo ?? 'Projeto sem repositório configurado no deploy'}
      >
        {custo.repo || 'sem repositório'}
      </div>
      {COMPONENTES.map((c) => (
        <Celula
          key={c.usd}
          dark={dark}
          valor={formatUsdBR(custo.detalhe?.[c.usd]) || '—'}
          detalhe={qtd(custo.quantidades?.[c.qtd], c.unidade)}
        />
      ))}
      <Celula dark={dark} valor={formatUsdBR(custo.estimadoMesUsd) || '—'} forte />
    </div>
  );
}

/**
 * Rodapé da tabela com a SOMA de cada componente. É a leitura que fechava a
 * conta de cabeça antes: com ela, a coluna se compara à barra de composição sem
 * somar cinco linhas na mão.
 */
function RodapeTotais({
  dark,
  linhas,
}: Readonly<{ dark: boolean; linhas: readonly RailwayProjectCost[] }>) {
  const p = makePalette(dark);
  const somas = COMPONENTES.map((c) =>
    linhas.reduce((acc, l) => acc + (l.detalhe?.[c.usd] || 0), 0),
  );
  const total = linhas.reduce((acc, l) => acc + (l.estimadoMesUsd || 0), 0);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 12,
        padding: '10px 16px',
        alignItems: 'center',
        background: p.surface2,
        borderTop: `1px solid ${p.borderStrong}`,
        fontSize: 12,
      }}
    >
      <div style={{ ...CABECALHO, color: p.muted }}>Soma</div>
      <div />
      {somas.map((s, i) => (
        <Celula key={COMPONENTES[i]?.usd} dark={dark} valor={formatUsdBR(s) || '—'} />
      ))}
      <Celula dark={dark} valor={formatUsdBR(total) || '—'} forte />
    </div>
  );
}

/** A mesma linha no celular — cada campo com o próprio rótulo. */
function CardProjeto({ custo, dark }: Readonly<{ custo: RailwayProjectCost; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: p.text, overflowWrap: 'anywhere' }}>
          {custo.projetoRailway}
        </div>
        <div
          style={{
            ...numeroTabular,
            fontSize: 13.5,
            fontWeight: 700,
            color: p.text,
            whiteSpace: 'nowrap',
          }}
        >
          {formatUsdBR(custo.estimadoMesUsd) || '—'}
        </div>
      </div>
      <div
        style={{ fontSize: 10.5, fontFamily: fontMono, color: p.muted, overflowWrap: 'anywhere' }}
      >
        {custo.repo || 'sem repositório'}
      </div>
      {COMPONENTES.map((c) => (
        <div
          key={c.usd}
          style={{ display: 'flex', justifyContent: 'space-between', gap: 10, minWidth: 0 }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 10.5,
              color: p.muted,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: corDe(c, dark),
                flexShrink: 0,
              }}
            />
            {c.nome}
          </span>
          <span
            style={{
              ...numeroTabular,
              fontSize: 11.5,
              color: p.textMuted,
              textAlign: 'right',
              overflowWrap: 'anywhere',
            }}
          >
            {`${formatUsdBR(custo.detalhe?.[c.usd]) || '—'} · ${qtd(custo.quantidades?.[c.qtd], c.unidade)}`}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Tabela de projetos (com o formato do celular embutido, como no inventário). */
function TabelaProjetos({
  linhas,
  dark,
  vazio,
}: Readonly<{ linhas: RailwayProjectCost[]; dark: boolean; vazio: string }>) {
  const p = makePalette(dark);
  const vp = useViewport();

  if (linhas.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: p.muted,
          fontSize: 12.5,
          border: `1px dashed ${p.border}`,
          borderRadius: 10,
        }}
      >
        {vazio}
      </div>
    );
  }

  if (vp.mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {linhas.map((c) => (
          <CardProjeto key={c.projetoRailway} custo={c} dark={dark} />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div style={SCROLLER}>
        <div style={{ minWidth: MIN_WIDTH }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 12,
              padding: '10px 16px',
              background: p.surface2,
              borderBottom: `1px solid ${p.border}`,
              color: p.muted,
              ...CABECALHO,
            }}
          >
            <div>Projeto</div>
            <div>Repositório</div>
            {COMPONENTES.map((c) => (
              <CabecalhoComponente key={c.usd} dark={dark} c={c} />
            ))}
            <div style={{ textAlign: 'right', color: p.textMuted }}>Total/mês</div>
          </div>
          {linhas.map((c, i) => (
            <LinhaProjeto
              key={c.projetoRailway}
              custo={c}
              dark={dark}
              divider={i < linhas.length - 1}
            />
          ))}
          <RodapeTotais dark={dark} linhas={linhas} />
        </div>
      </div>
    </div>
  );
}

/** Título de seção com subtítulo explicativo. */
function TituloSecao({
  dark,
  titulo,
  subtitulo,
}: Readonly<{ dark: boolean; titulo: string; subtitulo: string }>) {
  const p = makePalette(dark);
  return (
    <div style={{ marginBottom: 10, marginTop: 22 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: p.text, letterSpacing: '-0.012em' }}>
        {titulo}
      </div>
      <div style={{ fontSize: 12, color: p.muted, marginTop: 2 }}>{subtitulo}</div>
    </div>
  );
}

export function ScreenCosts({
  dark,
  accent = NOC_RED,
  costs,
  atualizando = false,
  onAtualizar,
}: Readonly<
  ScreenProps & {
    costs: RailwayCosts;
    /** `isFetching` da query — o botão de atualizar mostra o estado. */
    atualizando?: boolean;
    onAtualizar?: () => void;
  }
>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [dica, setDica] = useState<Dica | null>(null);

  // Ordem decrescente de custo: a pergunta da tela é "onde está o dinheiro".
  const ordenados = [...costs.custos].sort((a, b) => b.estimadoMesUsd - a.estimadoMesUsd);
  const naoMapeados = unmappedCosts(costs);
  // Mapeado = tem repositório no deploy (é o que casa com o inventário).
  const mapeados = ordenados.filter((c) => repoSlug(c.repo) !== null);
  const total = totalUsd(costs);
  const idade = idadeMs(costs.atualizadoEm);
  // Média aritmética simples sobre TODOS os projetos do workspace (mapeados ou
  // não): é a leitura de "quanto custa um projeto aqui". Sem projeto nenhum a
  // média é 0 e o card mostra '—' pela formatação.
  const mediaPorProjeto = costs.custos.length > 0 ? total / costs.custos.length : 0;

  // Composição e ranking olham o WORKSPACE INTEIRO, igual ao total do herói:
  // gráfico que soma diferente do número grande ao lado é erro de leitura
  // garantido.
  const totaisComponente = COMPONENTES.map((c) =>
    costs.custos.reduce((acc, l) => acc + (l.detalhe?.[c.usd] || 0), 0),
  );
  const ranking = montarRanking(ordenados);
  const temGrafico = !costs.semToken && total > 0;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="costs" dark={dark} accent={accent} role="admin" />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          title="Gastos"
          // A data também no subtítulo, e não só no indicador do topo: o
          // cabeçalho fica fixo, então a procedência do número continua à vista
          // com a página rolada (era o incômodo relatado). `formatDateTimeBR`
          // devolve '' quando `atualizadoEm` é null (caso `semToken`), e aí o
          // subtítulo volta a ser só a descrição, sem "atualizado em —".
          subtitle={
            formatDateTimeBR(costs.atualizadoEm)
              ? `Estimativa de custo dos projetos no Railway · atualizado em ${formatDateTimeBR(costs.atualizadoEm)} (${idadeTexto(idade)})`
              : 'Estimativa de custo dos projetos no Railway'
          }
          right={
            onAtualizar && (
              <ButtonGhost dark={dark} onClick={onAtualizar}>
                <IconRefresh size={13} /> {atualizando ? 'Atualizando…' : 'Atualizar'}
              </ButtonGhost>
            )
          }
        />

        <div
          style={{
            flex: 1,
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {/* ESTADO 1 · sem token: não há o que somar, e o texto diz o que fazer
              sem citar valor de segredo nenhum. */}
          {costs.semToken && (
            <Aviso dark={dark} tone="warn" titulo="Integração com o Railway não configurada">
              A variável de ambiente <strong>RAILWAY_API_TOKEN</strong> não está definida no serviço
              da API, então não há como consultar o consumo dos projetos. Um administrador precisa
              cadastrá-la nas variáveis do Railway (o valor é um token de API do workspace, e nunca
              aparece nesta tela). Depois disso os custos passam a ser lidos automaticamente, com
              cache de 1 hora.
            </Aviso>
          )}

          {/* ESTADO 2 · preço de disco/backup não confirmado: o total é PARCIAL,
              e isso tem de estar escrito antes de alguém levar o número adiante. */}
          {!costs.semToken && !costs.precoDiscoConfirmado && (
            <Aviso dark={dark} tone="warn" titulo="Disco e backup estão FORA do total">
              O preço por GB de disco e de backup ainda não foi confirmado, então estes dois itens
              entram apenas como quantidade consumida — o total abaixo cobre memória, CPU e egress.
              O gasto real do mês é, portanto, um pouco maior do que o exibido.
            </Aviso>
          )}

          {/* Snapshot velho: a rota serve o cache antigo quando a API do Railway
              falha, e isso chega como 200 — sem este aviso, o número parece
              fresco. */}
          {!costs.semToken && idade !== null && idade > IDADE_SUSPEITA_MS && (
            <Aviso dark={dark} tone="warn" titulo="Leitura possivelmente desatualizada">
              O servidor renova estes números a cada hora, mas a leitura em tela é de{' '}
              {idadeTexto(idade)}. Provavelmente a consulta à API do Railway falhou e o servidor
              preferiu servir o último dado bom a devolver erro. O consumo real pode estar maior.
            </Aviso>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
            {/* O ÚNICO número grande da tela. Sem token não existe leitura
                nenhuma, então o total é "—" e não "US$ 0,00": zero é uma
                afirmação sobre o gasto, e aqui não se sabe nada. */}
            <NumeroHeroi
              dark={dark}
              accent={accent}
              rotulo="Total do workspace por mês"
              valor={costs.semToken ? '—' : formatUsdBR(total) || '—'}
              nota={`estimativa do mês corrente · ${costs.moeda || 'USD'} · ${costs.custos.length} projetos`}
              compacto={vp.mobile}
            />
            <div
              style={{
                flex: '2 1 420px',
                minWidth: 0,
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                alignContent: 'stretch',
              }}
            >
              <Resumo
                dark={dark}
                rotulo="Projetos"
                valor={String(costs.custos.length)}
                nota={`${mapeados.length} com repositório · ${naoMapeados.length} sem`}
              />
              {/* Média por projeto no lugar do antigo card de "não mapeados":
                  com o de-para manual dos projetos que a API do Railway não
                  reporta, o não mapeado virou exceção (hoje zero), e um card
                  fixo em US$ 0,00 não informa nada. A seção de não mapeados
                  continua existindo mais abaixo e aparece se algum voltar. */}
              <Resumo
                dark={dark}
                rotulo="Média por projeto"
                valor={costs.semToken ? '—' : formatUsdBR(mediaPorProjeto) || '—'}
                nota={`${costs.custos.length} projeto${costs.custos.length === 1 ? '' : 's'} no workspace`}
              />
              <Resumo
                dark={dark}
                rotulo="Atualizado em"
                valor={formatDateTimeBR(costs.atualizadoEm) || '—'}
                nota={`${idadeTexto(idade)} · cache de 1 hora no servidor`}
              />
            </div>
          </div>

          {temGrafico && (
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                marginTop: 12,
              }}
            >
              <div style={{ flex: '1 1 100%', minWidth: 0 }}>
                <Cartao
                  dark={dark}
                  titulo="Composição do workspace"
                  subtitulo="Os cinco componentes cobrados somam o total do mês · passe o cursor para ver o valor e a participação"
                >
                  <BarraComposicao
                    dark={dark}
                    totais={totaisComponente}
                    total={total}
                    onDica={setDica}
                    onSair={() => setDica(null)}
                  />
                </Cartao>
              </div>
              <div style={{ flex: '1 1 100%', minWidth: 0 }}>
                <Cartao
                  dark={dark}
                  titulo="Projetos por custo do mês"
                  subtitulo={
                    ranking.some((i) => i.dobrados > 0)
                      ? `Ordem decrescente · os ${RANKING_VISIVEL} maiores, com o restante somado na última barra (a tabela abaixo mantém todos)`
                      : 'Ordem decrescente · custo estimado do mês por projeto'
                  }
                >
                  <RankingProjetos
                    dark={dark}
                    accent={accent}
                    itens={ranking}
                    total={total}
                    onDica={setDica}
                    onSair={() => setDica(null)}
                  />
                </Cartao>
              </div>
            </div>
          )}

          <TituloSecao
            dark={dark}
            titulo="Projetos por custo"
            subtitulo="Ordem decrescente · memória, CPU, egress, disco e backup somam o total de cada projeto"
          />
          <TabelaProjetos
            linhas={mapeados}
            dark={dark}
            vazio={
              costs.semToken
                ? 'Sem dados: configure o token da API do Railway.'
                : 'Nenhum projeto com repositório configurado.'
            }
          />

          {naoMapeados.length > 0 && (
            <>
              <TituloSecao
                dark={dark}
                titulo="Projetos não mapeados"
                subtitulo="Sem repositório configurado no deploy, então não casam com nenhuma aplicação do inventário — mas consomem e contam no total"
              />
              <TabelaProjetos
                linhas={naoMapeados}
                dark={dark}
                vazio="Nenhum projeto sem repositório."
              />
            </>
          )}

          {/* Procedência do número, no rodapé: é ESTIMATIVA, e o cache é de 1
              hora (nada de polling — o botão Atualizar é a única forma de forçar). */}
          <div
            style={{
              ...numeroTabular,
              marginTop: 18,
              fontSize: 11,
              color: p.muted,
              lineHeight: 1.7,
            }}
          >
            <div>
              <span style={{ color: WARN, fontWeight: 700 }}>Estimativa</span> — uso medido pela API
              do Railway multiplicado pela tabela de preços do plano. Não é a fatura.
            </div>
            <div>
              Valores em {costs.moeda || 'USD'} · leitura de{' '}
              {formatDateTimeBR(costs.atualizadoEm) || 'data desconhecida'} ({idadeTexto(idade)}) ·
              o servidor guarda a resposta por 1 hora.
            </div>
          </div>
        </div>
      </div>
      <Tooltip dica={dica} dark={dark} />
    </div>
  );
}
